import { PlusIcon } from '@heroicons/react/24/outline'
import { cn } from '@/lib/utils'
import { SearchableSelect, SearchableSelectOption } from './SearchableSelect'

interface DefaultItem {
  id: string | number
  name: string
  description?: string
}

interface SelectWithAddButtonProps {
  value?: number | string | null
  onChange: (_value: number | string | (number | string)[] | null) => void
  fetcher: (_search: string) => Promise<SearchableSelectOption[]>
  onAdd: () => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  defaultValue?: DefaultItem | string | number | null
  error?: string
  dropdownPosition?: 'top' | 'bottom' | 'auto'
  name?: string
}

/**
 * SelectWithAddButton - Componente genérico reutilizable
 *
 * Combina un selector searchable con un botón de agregar (+)
 * Ideal para seleccionar elementos con la opción de crear nuevos
 *
 * @example
 * ```tsx
 * <SelectWithAddButton
 *   value={originId}
 *   onChange={setOriginId}
 *   fetcher={fetchOrigins}
 *   onAdd={() => setShowCreateDialog(true)}
 *   placeholder="Seleccionar origen"
 * />
 * ```
 */
export function SelectWithAddButton({
  value,
  onChange,
  fetcher,
  onAdd,
  placeholder = 'Seleccionar...',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'No se encontraron resultados',
  disabled = false,
  className,
  defaultValue,
  error,
  dropdownPosition = 'bottom',
  name = '',
}: SelectWithAddButtonProps) {
  return (
    <div className={cn('flex flex-row items-start gap-2', className)}>
      {/* Select & Combobox */}
      <div className="flex-1">
        <SearchableSelect
          defaultValue={defaultValue}
          disabled={disabled}
          dropdownPosition={dropdownPosition}
          emptyMessage={emptyMessage}
          error={error}
          fetcher={fetcher}
          name={name}
          onChange={onChange}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          value={value}
        />
      </div>

      {/* Icon Button */}
      <button
        className={cn(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center',
          'rounded-md border border-neutral-300 bg-neutral-50',
          'shadow-sm transition-colors',
          'focus:outline-none focus:ring-2 hover:bg-neutral-100',
          'focus:ring-neutral-500 focus:ring-offset-1',
          {
            'cursor-not-allowed opacity-50': disabled,
          }
        )}
        disabled={disabled}
        onClick={onAdd}
        type="button"
      >
        <PlusIcon className="h-4 w-4 text-neutral-900" />
      </button>
    </div>
  )
}
